import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Camera, CloudOff, LifeBuoy, MapPinned, ShieldCheck, Tags } from 'lucide-react';
import BrandMark from '../common/BrandMark';
import ShelfLogin from '../login/login';
import './landing.css';

const APP_STORE_URL = 'https://apps.apple.com/app/id6754645276';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.anaisabeliturbec.shelf';

const SHOTS = {
    route: { src: '/landing/01-pisos-de-venta.jpg', alt: 'Ruta del día con los pisos de venta asignados' },
    shelf: { src: '/landing/02-anaquel.jpg', alt: 'Auditoría de anaquel por categoría y producto' },
    price: { src: '/landing/03-registro-de-precio.jpg', alt: 'Registro de precio de un producto' },
    competition: { src: '/landing/04-precios-competencia.jpg', alt: 'Precios de la competencia en el piso de venta' }
};

const FEATURES = [
    { icon: <MapPinned />, title: 'Ruta del día', text: 'Cada visita ordenada por distancia y el avance del recorrido a simple vista.' },
    { icon: <Tags />, title: 'Anaquel y precios', text: 'Disponibilidad, precios propios y de la competencia, producto por producto.' },
    { icon: <Camera />, title: 'Evidencia fotográfica', text: 'Fotos de exhibiciones y espacios comprimidas y enviadas sin fricción.' },
    { icon: <CloudOff />, title: 'Funciona sin señal', text: 'El trabajo se guarda en el teléfono y se sincroniza al volver la conexión.' }
];

/** iPhone-style frame around an App Store screenshot. */
function Phone({ shot, className = '', priority = false }) {
    return (
        <div className={`lp-phone ${className}`}>
            <div className="lp-phone-screen">
                <img src={shot.src} alt={shot.alt} loading={priority ? 'eager' : 'lazy'} decoding="async" draggable="false" />
                <span className="lp-phone-island" aria-hidden="true" />
            </div>
        </div>
    );
}

function AppleLogo() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true" className="lp-store-icon">
            <path
                fill="currentColor"
                d="M16.36 12.68c-.02-2.3 1.88-3.4 1.97-3.46-1.07-1.57-2.74-1.78-3.33-1.8-1.42-.14-2.77.83-3.49.83-.72 0-1.83-.81-3.01-.79-1.55.02-2.98.9-3.78 2.29-1.61 2.8-.41 6.94 1.16 9.21.77 1.11 1.69 2.36 2.89 2.31 1.16-.05 1.6-.75 3-.75 1.4 0 1.79.75 3.01.73 1.25-.02 2.04-1.13 2.8-2.25.88-1.29 1.24-2.53 1.26-2.6-.03-.01-2.42-.93-2.48-3.72zM14.06 5.92c.64-.78 1.07-1.85.95-2.92-.92.04-2.04.61-2.7 1.39-.59.69-1.11 1.79-.97 2.84 1.03.08 2.07-.52 2.72-1.31z"
            />
        </svg>
    );
}

function PlayLogo() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true" className="lp-store-icon">
            <path fill="currentColor" d="M4.6 2.3 13.9 11.6 4.6 20.9c-.37-.2-.6-.6-.6-1.07V3.37c0-.47.23-.87.6-1.07z" opacity=".95" />
            <path fill="currentColor" d="m16.9 8.6-3 3 3 3 3.56-2.02c.72-.42.72-1.54 0-1.96L16.9 8.6z" opacity=".75" />
            <path fill="currentColor" d="M4.6 2.3c.3-.17.68-.18 1.04.03L16.9 8.6l-3 3-9.3-9.3z" opacity=".85" />
            <path fill="currentColor" d="m13.9 11.6 3 3-11.26 6.27c-.36.21-.74.2-1.04.03l9.3-9.3z" opacity=".6" />
        </svg>
    );
}

function StoreBadges() {
    return (
        <div className="lp-stores">
            <a className="lp-store" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
                <AppleLogo />
                <span>
                    <small>Descárgala en el</small>App Store
                </span>
            </a>
            <a className="lp-store" href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
                <PlayLogo />
                <span>
                    <small>Disponible en</small>Google Play
                </span>
            </a>
        </div>
    );
}

/**
 * Public landing at shelfve.com: product story plus the sign-in card. Signing in looks the email up
 * in central-service and sends each company's users to their own admin. "Acceder" opens the
 * standalone sign-in page (/login), the same form without the landing around it.
 */
export default function Landing() {
    return (
        <div className="lp">
            <div className="lp-backdrop" aria-hidden="true" />

            <header className="lp-nav">
                <a href="/" className="lp-brand" aria-label="Shelf, inicio">
                    <BrandMark size={34} />
                    <span>Shelf</span>
                </a>
                <nav className="lp-nav-links">
                    <a href="#producto">Producto</a>
                    <a href="/support">Soporte</a>
                    <Link to="/login" className="lp-nav-cta">
                        Acceder
                    </Link>
                </nav>
            </header>

            <main>
                <section className="lp-hero">
                    <div className="lp-hero-copy">
                        <span className="lp-eyebrow lp-reveal">
                            <span className="lp-eyebrow-dot" />
                            Ejecución en punto de venta
                        </span>
                        <h1 className="lp-title lp-reveal lp-d1">
                            Tu piso de venta, <span className="lp-title-accent">en tiempo real.</span>
                        </h1>
                        <p className="lp-lead lp-reveal lp-d2">
                            Shelf conecta a tu equipo con tu operación: rutas, anaqueles, precios y evidencia fotográfica,
                            sincronizados al instante, incluso sin señal.
                        </p>

                        <div id="acceso" className="lp-login lp-reveal lp-d3">
                            <ShelfLogin embedded />
                        </div>
                    </div>

                    <div className="lp-stage" aria-label="Capturas de la app Shelf">
                        <div className="lp-glow" aria-hidden="true" />
                        <Phone shot={SHOTS.shelf} className="lp-phone-back lp-phone-left" />
                        <Phone shot={SHOTS.price} className="lp-phone-back lp-phone-right" />
                        <Phone shot={SHOTS.route} className="lp-phone-front" priority />

                        <div className="lp-chip lp-chip-a" aria-hidden="true">
                            <span className="lp-chip-icon lp-chip-icon-ok">✓</span>
                            <span>
                                <strong>Anaquel completo</strong>
                                <small>15 de 15 productos</small>
                            </span>
                        </div>
                        <div className="lp-chip lp-chip-b" aria-hidden="true">
                            <span className="lp-ring" style={{ '--p': 86 }}>
                                <span>86%</span>
                            </span>
                            <span>
                                <strong>Recorrido de hoy</strong>
                                <small>6 de 7 pisos de venta</small>
                            </span>
                        </div>
                    </div>
                </section>

                <section id="producto" className="lp-section">
                    <div className="lp-section-head">
                        <span className="lp-kicker">Hecho para el trabajo en campo</span>
                        <h2>Todo lo que pasa en el anaquel, en un solo lugar.</h2>
                    </div>
                    <div className="lp-features">
                        {FEATURES.map((f) => (
                            <article key={f.title} className="lp-feature">
                                <span className="lp-feature-icon">{f.icon}</span>
                                <h3>{f.title}</h3>
                                <p>{f.text}</p>
                            </article>
                        ))}
                    </div>
                </section>

                <section className="lp-section lp-split">
                    <div className="lp-split-media">
                        <div className="lp-glow lp-glow-soft" aria-hidden="true" />
                        <Phone shot={SHOTS.competition} className="lp-phone-solo" />
                    </div>
                    <div className="lp-split-copy">
                        <span className="lp-kicker">Inteligencia de precios</span>
                        <h2>Conoce la competencia en cada visita.</h2>
                        <p>
                            Registra precios propios y de la competencia en segundos. Tu equipo comercial los ve en el panel de
                            Shelf con historial, filtros y exportación lista para el análisis.
                        </p>
                        <ul className="lp-checks">
                            <li>
                                <ShieldCheck /> Datos de cada empresa aislados y protegidos
                            </li>
                            <li>
                                <ShieldCheck /> Reportes y exportación a Excel desde el panel
                            </li>
                            <li>
                                <ShieldCheck /> Apps para iOS y Android
                            </li>
                        </ul>
                    </div>
                </section>

                <section className="lp-section">
                    <div className="lp-cta">
                        <div>
                            <h2>¿Eres parte del equipo?</h2>
                            <p>Descarga la app e inicia sesión con la cuenta que te dio tu empresa.</p>
                        </div>
                        <div className="lp-cta-actions">
                            <StoreBadges />
                            <Link to="/login" className="lp-cta-link">
                                Acceder al panel <ArrowRight />
                            </Link>
                        </div>
                    </div>
                </section>
            </main>

            <footer className="lp-footer">
                <div className="lp-brand lp-brand-sm">
                    <BrandMark size={26} />
                    <span>Shelf</span>
                </div>
                <nav>
                    <a href="/support">
                        <LifeBuoy /> Soporte
                    </a>
                    <a href="/privacy-policy">Privacidad</a>
                </nav>
                <p>© {new Date().getFullYear()} Shelf. Todos los derechos reservados.</p>
            </footer>
        </div>
    );
}
